// Gating granular por recurso para operadores do portal do banco.
//
// O VOCABULARIO (recursos, presets, deteccao de preset) mudou-se para
// `@atlas/types/banco-recursos` em 15/09/2026, quando o servidor passou a
// CONFERIR a permissao em vez de so' a web esconder menu. Enquanto a lista
// vivia duplicada aqui e em `apps/api/.../cadastros.ts`, divergir custava um
// rotulo errado; com o portao ligado, divergir vira falha de autorizacao — a
// web esconderia o que a API libera, ou o contrario.
//
// O que sobra neste arquivo e' o que e' de CLIENTE: os rotulos do dropdown e a
// leitura do JWT que decide o menu. Mesma divisao que `prefeitura-perms.ts`.

import {
  BANCO_PRESETS,
  BANCO_RESOURCE_GROUPS,
  BANCO_TODAS_PERMISSOES,
  bancoEscopoNoModo,
  bancoGruposDoModo,
  bancoPermissaoCobre,
  bancoPresetsDoModo,
  bancoRecursoNoModo,
  detectarBancoPreset,
  ehPresetCustom,
  type BancoEscopoRecurso,
  type BancoModoPortal,
  type BancoPerfilLabel,
} from "@atlas/types";

export {
  BANCO_PRESETS,
  BANCO_RESOURCE_GROUPS,
  BANCO_TODAS_PERMISSOES,
  bancoEscopoNoModo,
  bancoGruposDoModo,
  bancoPermissaoCobre,
  bancoPresetsDoModo,
  bancoRecursoNoModo,
  detectarBancoPreset,
  ehPresetCustom,
};
export type { BancoEscopoRecurso, BancoModoPortal, BancoPerfilLabel };

export const BANCO_PRESET_LABELS: { value: BancoPerfilLabel; label: string; descricao: string }[] = [
  { value: "admin",         label: "Admin",         descricao: "Acesso total ao portal (wildcard *)." },
  { value: "operador",      label: "Operador",      descricao: "Propostas, ofertas, margem, carteira, convenios." },
  { value: "consulta",      label: "Consulta",      descricao: "Leitura de propostas, carteira, convenios." },
  { value: "consulta_margem", label: "Consulta de margem", descricao: "So' consulta a margem dos servidores. Nao averba nem opera contratos." },
  { value: "relatorios",    label: "Relatorios",    descricao: "Relatorios + bate-carteira. Sem CRUD." },
  { value: "operadora",     label: "Benefícios — operador", descricao: "Benefícios, pedidos de cancelamento e troca de arquivos, inclusive o envio do arquivo mensal." },
  { value: "operadora_consulta", label: "Benefícios — consulta", descricao: "Vê benefícios e pedidos de cancelamento e baixa retornos. Não encerra, não responde pedidos, não envia arquivo." },
  { value: "personalizado", label: "Personalizado", descricao: "Ponto de partida vazio — marque caixa a caixa." },
];

/**
 * Permissoes do operador logado, lidas do JWT.
 *
 * `undefined` quando o token e' do login-MASTER do banco (o `bancos.loginEmail`),
 * que nao tem a claim — e ai `bancoPermissaoCobre` devolve `true` para tudo. E'
 * a mesma retrocompatibilidade que a organizacao pratica, e o que impede o
 * portao de derrubar o acesso de todo banco ja cadastrado.
 */
export function readBancoPermissoesFromJwt(
  accessToken: string | null | undefined,
): string[] | undefined {
  return lerClaimDoBanco(accessToken, (p) =>
    Array.isArray(p.banco_permissoes) ? (p.banco_permissoes as string[]) : undefined,
  );
}

/** Permissoes do operador da sessao atual (token salvo pelo SDK). */
export function readBancoPermissoesDaSessao(): string[] | undefined {
  try {
    const stored = window.localStorage.getItem("atlas:tokens");
    return readBancoPermissoesFromJwt(stored ? JSON.parse(stored)?.access_token : null);
  } catch {
    return undefined;
  }
}

/** Mesmo portao da API: recurso `refinanciamento`, ou o grupo `propostas` inteiro. */
export function bancoPodeRefinanciar(permissoes: readonly string[] | undefined): boolean {
  return bancoPermissaoCobre(permissoes, "refinanciamento") || bancoPermissaoCobre(permissoes, "propostas");
}

/** Mesmo portao da API (`MARGEM_LEITURA`): buscar e ver a ficha do servidor. */
export function bancoPodeConsultarMargem(permissoes: readonly string[] | undefined): boolean {
  return bancoPermissaoCobre(permissoes, "margem-contratacao") || bancoPermissaoCobre(permissoes, "margem-consulta");
}

/** Averbar pela ficha exige `margem-contratacao` — `margem-consulta` so' le. */
export function bancoPodeAverbarPelaFicha(permissoes: readonly string[] | undefined): boolean {
  return bancoPermissaoCobre(permissoes, "margem-contratacao");
}

/** Mesmo portao da API para `GET /contratos`: `carteira` ou `propostas`. */
export function bancoPodeVerContratos(permissoes: readonly string[] | undefined): boolean {
  return bancoPermissaoCobre(permissoes, "carteira") || bancoPermissaoCobre(permissoes, "propostas");
}

/**
 * A sessao esta presa na troca de senha?
 *
 * Quem decide de verdade e' a API (`requireBancoPermissao` responde 403
 * `senha_temporaria_pendente`). Ler a claim aqui serve para levar a pessoa a
 * tela certa em vez de mostrar um portal que responde 403 em tudo.
 */
export function readBancoSenhaTemporariaFromJwt(
  accessToken: string | null | undefined,
): boolean {
  return lerClaimDoBanco(accessToken, (p) => p.banco_senha_temporaria === true) ?? false;
}

function lerClaimDoBanco<T>(
  accessToken: string | null | undefined,
  extrair: (payload: Record<string, unknown>) => T,
): T | undefined {
  if (!accessToken) return undefined;
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return undefined;
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return extrair(JSON.parse(atob(padded)));
  } catch {
    // Token ilegivel nao vira "sem permissao": quem nao tem token nao passa pelo
    // RequireAuth, e quem passou tem um token que o servidor validou. Cair aqui
    // e' bug de parsing nosso, e bloquear o menu inteiro por isso seria pior —
    // alem de nao proteger nada, porque o portao de verdade esta na API.
    return undefined;
  }
}
